import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import {
  FormGroup,
  FormHelpText,
  FormInput,
  FormPopoverTrigger,
  FormTextarea,
  Popover,
} from '@spotify-internal/encore-web';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import {
  convertPlaylistV1ToV2,
  getValidSpotifyUri,
  isSpotifyUri,
  splitTextByDelim,
} from 'components/ContentReporting/validation-utils';

type URIFieldProps = {
  label: string;
  isFieldValid: (uris: string[]) => boolean;
  value: string[];
  maxURIsAmount?: number;
  translationPrefix?: string;
  onChange: (value: string[]) => void;
  errorMessage?: string;
  helperMessage?: string;
  isValidUriMessage?: string;
  popoverMessage?: string;
};

const URIField: FunctionComponent<URIFieldProps> = props => {
  const {
    label,
    isFieldValid,
    maxURIsAmount = 1,
    translationPrefix,
    onChange,
    value,
    errorMessage,
    helperMessage,
    isValidUriMessage,
    popoverMessage = 'content_uris_tooltip',
  } = props;

  const [inputValue, setInputValue] = useState<string>('');
  const [isValidUri, setIsValidUri] = useState<boolean>(true);
  const [isPopoverShown, setIsPopoverShown] = useState<boolean>(false);

  const { t } = useTranslation(translationPrefix);

  const formatUris = (uris: string[]) => {
    return uris
      .filter(Boolean)
      .map(uri => (isSpotifyUri(uri) ? getValidSpotifyUri(uri) : uri))
      .map(convertPlaylistV1ToV2);
  };

  const onChangeUris = (uris: string[]) => {
    const isValid = isFieldValid(uris);
    setIsValidUri(isValid);
    onChange(formatUris(uris));
  };

  const handleDebounceOnChangeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    const splittedUris = splitTextByDelim(event.target.value);

    onChangeUris(formatUris(splittedUris));
  };

  useEffect(() => {
    const defaultValue = value || [];
    onChangeUris(defaultValue);
    setInputValue(defaultValue.join('\n'));
    // In order to allow the hook on the Component Did Mount lifecyle step the useEffect can't have a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceOnChange = useCallback(debounce(handleDebounceOnChangeEvent, 500), []);

  const Field = maxURIsAmount > 1 ? FormTextarea : FormInput;
  const inputId = `${t(label)}-input`;

  return (
    <FormGroup
      required
      label={t(label)}
      labelFor={inputId}
      popover={
        <FormPopoverTrigger
          placement="end"
          overlay={isPopoverShown && <Popover arrow={Popover.left}>{t(popoverMessage)}</Popover>}
          onShow={() => setIsPopoverShown(true)}
          onHide={() => setIsPopoverShown(false)}
          aria-label="Help"
        />
      }
    >
      <Field
        id={inputId}
        type="text"
        required
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setInputValue(event.target.value);
          debounceOnChange(event);
        }}
        value={inputValue}
        error={!isValidUri}
      />

      {!isValidUri && (
        <FormHelpText id="html-validation-1-message" error={!isValidUri}>
          {!!isValidUriMessage ? (
            <span dangerouslySetInnerHTML={{ __html: isValidUriMessage }} />
          ) : (
            t('uri_invalid_generic_dynamic_uri_number', { max_uri_number: maxURIsAmount })
          )}
        </FormHelpText>
      )}

      {!!helperMessage && (
        <FormHelpText id="html-validation-1-message">
          <span dangerouslySetInnerHTML={{ __html: helperMessage }} />
        </FormHelpText>
      )}

      {!!errorMessage && (
        <FormHelpText id="html-validation-1-message" error>
          {t(errorMessage)}
        </FormHelpText>
      )}
    </FormGroup>
  );
};

export default URIField;
